import { useTheme } from '@mui/material';
import { memo, useCallback } from 'react';

export interface CheckboxProps {
  checked: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  /** Accessible name; the box has no visible text of its own. */
  label: string;
  onChange?: () => void;
}

export const Checkbox = memo(function Checkbox({
  checked,
  disabled = false,
  indeterminate = false,
  label,
  onChange,
}: CheckboxProps) {
  const stop = useCallback((e: React.SyntheticEvent) => e.stopPropagation(), []);
  const theme = useTheme();

  const active = !!checked || !!indeterminate;

  const border = `2px solid ${disabled ? theme.palette.grey[300] : theme.palette.primary.dark}`;

  const bg = disabled
    ? theme.palette.grey[100]
    : active
    ? theme.palette.primary.dark
    : theme.palette.common.white;

  const glyphColor = disabled ? theme.palette.grey[500] : theme.palette.common.white;

  const cursor = disabled ? 'not-allowed' : 'pointer';

  const handleChange = () => {
    if (onChange) {
      onChange();
    }
  };

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        stop(e);
        if (!disabled) handleChange();
      }}
      onPointerDownCapture={stop}
      onKeyDownCapture={stop}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          handleChange();
        }
      }}
      tabIndex={disabled ? -1 : 0}
      style={{
        width: 40,
        height: 40,
        display: 'grid',
        placeItems: 'center',
        border: 'none',
        borderRadius: '50%',
        background: 'transparent',
        cursor,
        transition: 'background-color 120ms',
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = theme.palette.grey[100];
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = 'transparent';
        }
      }}
    >
      <div
        style={{
          width: 16,
          height: 16,
          display: 'grid',
          placeItems: 'center',
          border,
          borderRadius: 4,
          background: bg,
          color: glyphColor,
          boxShadow: 'none',
          transition: 'background-color 120ms, border-color 120ms, box-shadow 120ms',
          lineHeight: 0,
          pointerEvents: 'none', // Let the parent button handle all interactions
        }}
      >
        {/* Check (exactly centered in 16x16) */}
        {checked && !indeterminate ? (
          <svg
            viewBox="0 0 16 16"
            width="12"
            height="12"
            aria-hidden="true"
            focusable="false"
            style={{ display: 'block', pointerEvents: 'none' }}
          >
            <path
              d="M12.6 4.6 L6.9 10.3 L3.4 7.6"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              vectorEffect="non-scaling-stroke"
              shapeRendering="geometricPrecision"
            />
          </svg>
        ) : null}

        {/* Indeterminate dash (centered at y=8) */}
        {indeterminate ? (
          <svg
            viewBox="0 0 16 16"
            width="12"
            height="12"
            aria-hidden="true"
            focusable="false"
            style={{ display: 'block', pointerEvents: 'none' }}
          >
            {/* centered bar: y = 8 - (h/2) */}
            <rect
              x="3"
              y="7"
              width="10"
              height="2"
              rx="1"
              fill="currentColor"
              shapeRendering="geometricPrecision"
            />
          </svg>
        ) : null}
      </div>
    </button>
  );
});
